import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { AnimatedCounter } from '@/components/shared/AnimatedCounter'
import { VisitorOrb } from '@/components/dashboard/VisitorOrb'
import { useTheme } from '@/lib/contexts/ThemeContext'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HugeiconsIcon } from "@hugeicons/react"
import {
  GlobalIcon,
  ViewIcon,
  Cursor01Icon,
  Note01Icon,
  ComputerIcon,
  SmartPhone01Icon,
  ArrowRight02Icon,
} from "@hugeicons/core-free-icons"
import { OverviewTabs, OverviewCardTitle, OverviewCardFooter } from '@/components/shared/overview/OverviewBreakdownCard'
import { formatNumeric } from '@/lib/utils/stringUtils'
import {
  buildVisitorProfiles,
  parseRealtimeFeed,
  profileKeyOf,
  visitorName,
  type RealtimeFeedEvent,
  type VisitorProfile,
} from '@/lib/realtime/visitorProfiles'
import { OnSitePill, countryName as nameOfCountry, useNow } from './VisitorProfileSheet'

import { getAggregateTrafficRealtime } from '@/lib/api/external-api'
import { getElapsedTime } from '@/lib/utils/timeUtils'
import { countryCodes } from '@/lib/constants/countries'
import { countryCoordinates } from '@/lib/constants/countryCoordinates'
import {
  getCountryFlagEmoji,
  getVisitorAvatarGradient,
  visitorAvatarSeed,
  VISITOR_AVATAR_GRAIN,
} from '@/lib/utils/visitorAvatar'
import { clickedPhrase } from '@/components/heatmaps/heatmapInsights'


type RealtimeEvent = RealtimeFeedEvent

interface RealTimeDashboardProps {
  propertyId: string | undefined
  token: string | undefined
  filters?: any[]
  className?: string
  anomalyData?: {
    type: 'up' | 'down'
    percentChange: number
  } | null
  // Multi-site aggregate mode
  isAggregateMode?: boolean
  propertyIds?: string[]
  /** Compact pulse strip (~280px, no globe) */
  compact?: boolean
  /**
   * `overview`: the Analytics Overview live card (Paper "App v3 · Analytics
   * Overview"): on-site-now pane with a 30-minute histogram beside the feed.
   */
  variant?: 'default' | 'overview'
}

type FeedFilter = 'all' | 'views' | 'clicks' | 'forms'
type LiveView = 'activity' | 'visitors'

const LIVE_VIEWS: { value: LiveView; label: string }[] = [
  { value: 'activity', label: 'Activity' },
  { value: 'visitors', label: 'Visitors' },
]

const FEED_FILTERS: { value: FeedFilter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'views', label: 'Page views' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'forms', label: 'Forms' },
]

const HISTOGRAM_BUCKETS = 16
const HISTOGRAM_WINDOW_MS = 30 * 60 * 1000

/** "12s", "4m", "3h", "2d" */
function shortElapsed(time: string): string {
  const ms = Date.now() - new Date(time).getTime()
  if (!Number.isFinite(ms) || ms < 0) return 'now'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

function eventKind(e: { type: string; action?: string }): Exclude<FeedFilter, 'all'> {
  if (e.type === 'session') return 'views'
  return e.action === 'click' ? 'clicks' : 'forms'
}

function isMobileDevice(device?: string): boolean {
  const d = (device || '').toLowerCase()
  return d.includes('ios') || d.includes('android') || d.includes('mobile') || d.includes('ipad') || d.includes('tablet')
}

// Arc for activity visualization (like Shopify)
interface ActivityArc {
  id: string
  startLat: number
  startLng: number
  endLat: number
  endLng: number
  color: string
}

// Ring for location pulse
interface ActivityRing {
  id: string
  lat: number
  lng: number
  color: string
}

// Random origin points around the globe edges - creates variety
const ARC_ORIGINS = [
  { lat: 70, lng: -120 },  // Arctic/Alaska
  { lat: 60, lng: 30 },    // Northern Europe
  { lat: -45, lng: 170 },  // New Zealand area
  { lat: 35, lng: 140 },   // Japan
  { lat: -30, lng: -60 },  // South America
  { lat: 50, lng: -40 },   // North Atlantic
]

/** Factual visitor-feed row: gradient avatar · “Visitor from {Country}” · source · action · time. */
function ActivityEvent({ event, isNew, index, getEventDescription, getElapsedTime, getCountryName, onOpen }: {
  onOpen: () => void
  event: RealtimeEvent
  isNew: boolean
  index: number
  getEventDescription: (e: RealtimeEvent) => string
  getElapsedTime: (t: string) => string
  getCountryName: (code: string) => string
}) {
  const flag = getCountryFlagEmoji(event.country)
  const countryLabel = event.country ? getCountryName(event.country) : ''
  const placeHint = event.region
    ? `${event.region}, ${countryLabel}`
    : countryLabel || undefined
  const headline = countryLabel
    ? `Visitor from ${countryLabel}`
    : event.type === 'session'
      ? 'Visitor'
      : 'Activity'
  const actionLine = getEventDescription(event)
  const source = event.utmSource?.trim()
  const avatarSeed = visitorAvatarSeed({
    id: event.id,
    country: event.country,
    region: event.region,
  })
  const avatarGradient = getVisitorAvatarGradient(avatarSeed)

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{
        duration: 0.18,
        delay: Math.min(index, 8) * 0.02,
        ease: [0.23, 1, 0.32, 1],
        height: { duration: 0.12 },
      }}
      className={cn(
        'group flex w-full items-center gap-2.5 px-3 py-2.5 text-left focus-visible:bg-muted/40 focus-visible:outline-none',
        'border-b border-border/20 last:border-b-0',
        'transition-colors duration-150 hover:bg-muted/30',
        isNew && 'bg-success/[0.06]',
      )}
    >
      <div
        className="relative size-8 shrink-0"
        title={placeHint}
        aria-hidden
      >
        <div className="relative size-8 overflow-hidden rounded-full border border-border/30 ring-1 ring-black/5 dark:ring-white/10">
          <div
            className="absolute inset-0"
            style={{ backgroundImage: avatarGradient }}
          />
          {/* Film grain — CSS overlay only (no per-row SVG filters) */}
          <div
            className="absolute inset-0 opacity-[0.42] mix-blend-overlay dark:opacity-[0.5] dark:mix-blend-soft-light pointer-events-none"
            style={{
              backgroundImage: VISITOR_AVATAR_GRAIN,
              backgroundSize: '64px 64px',
            }}
          />
        </div>
        {flag ? (
          <span className="absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-card border border-border/40 text-xs leading-none shadow-sm">
            {flag}
          </span>
        ) : (
          <span className="absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-card border border-border/40 shadow-sm">
            <HugeiconsIcon icon={GlobalIcon} size={8} className="text-muted-foreground" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs font-medium text-foreground truncate leading-tight">
            {headline}
          </span>
          {source ? (
            <span className="shrink-0 inline-flex h-[18px] items-center rounded-md border border-border/40 bg-muted/40 px-1.5 text-xs font-medium text-muted-foreground capitalize">
              {source}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground truncate leading-snug mt-0.5">
          {actionLine}
        </p>
      </div>

      <span className="text-xs text-muted-foreground/70 tabular-nums text-right leading-none shrink-0 self-start pt-0.5">
        {getElapsedTime(event.time)}
      </span>
    </motion.button>
  )
}

export default function RealTimeDashboard({ propertyId, token, filters = [], className, anomalyData, isAggregateMode = false, propertyIds = [], compact = false, variant = 'default' }: RealTimeDashboardProps) {
  const isOverview = variant === 'overview'
  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all')
  const [liveView, setLiveView] = useState<LiveView>('activity')
  const navigate = useNavigate()
  const location = useLocation()
  const [overviewExpanded, setOverviewExpanded] = useState(false)
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([])
  const [activeVisitors, setActiveVisitors] = useState<number | null>(null)
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set())
  const [activityArcs, setActivityArcs] = useState<ActivityArc[]>([])
  const [activityRings, setActivityRings] = useState<ActivityRing[]>([])
  const [feedDisplayLimit, setFeedDisplayLimit] = useState(compact ? 7 : 14)

  // WebGL support detection (some users/browsers block WebGL entirely)
  const webglSupported = useMemo(() => {
    if (typeof window === 'undefined') return false
    try {
      const canvas = document.createElement('canvas')
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      )
    } catch {
      return false
    }
  }, [])

  // Globe visibility toggle with localStorage persistence (default: off / list view).
  // Compact Overview pulse never shows the globe.
  const [showGlobe, setShowGlobe] = useState(() => {
    if (compact || variant === 'overview') return false
    if (typeof window === 'undefined') return false
    try {
      const stored = localStorage.getItem('humblytics-globe-visible')
      return stored !== null ? JSON.parse(stored) : false
    } catch {
      return false
    }
  })
  const globeVisible = compact || variant === 'overview' ? false : showGlobe

  const toggleGlobe = useCallback(() => {
    setShowGlobe((prev: boolean) => {
      const newValue = !prev
      try {
        localStorage.setItem('humblytics-globe-visible', JSON.stringify(newValue))
      } catch {
        // Ignore localStorage errors
      }
      return newValue
    })
  }, [])

  // Memoize propertyIds array to prevent unnecessary re-renders
  const memoizedPropertyIds = useMemo(() => propertyIds, [JSON.stringify(propertyIds)])
  
  // Globe component (dynamically loaded)
  const [GlobeComponent, setGlobeComponent] = useState<any>(null)
  const globeEl = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const refreshInterval = useRef<NodeJS.Timeout | null>(null)
  
  // Dynamically import react-globe.gl only when WebGL is available
  useEffect(() => {
    if (!webglSupported || variant === 'overview') return
    import('react-globe.gl').then((module) => {
      setGlobeComponent(() => module.default)
    }).catch(() => {
      // Globe failed to load — WebGL may have been revoked after page load
    })
  }, [webglSupported, variant])
  
  // Active visitors in last 5 minutes

  // Top location from recent events
  const topLocation = useMemo(() => {
    const countryCounts: Record<string, number> = {}
    realtimeEvents.forEach(e => {
      if (e.country) {
        countryCounts[e.country] = (countryCounts[e.country] || 0) + 1
      }
    })
    const sorted = Object.entries(countryCounts).sort((a, b) => b[1] - a[1])
    return sorted[0]?.[0] || null
  }, [realtimeEvents])

  const topLocationFlag = topLocation ? getCountryFlagEmoji(topLocation) : ''

  // Session vs event counts
  const eventCounts = useMemo(() => {
    const sessions = realtimeEvents.filter(e => e.type === 'session').length
    const clicks = realtimeEvents.filter(e => e.type === 'metric' && e.action === 'click').length
    const forms = realtimeEvents.filter(e => e.type === 'metric' && e.action !== 'click').length
    return { sessions, clicks, forms }
  }, [realtimeEvents])

  // Points data for the globe (visitor locations) - only show if there are active visitors
  const pointsData = useMemo(() => {
    // Only show points for events within the last 5 minutes (active visitors)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
    const activeEvents = realtimeEvents.filter(e => 
      new Date(e.time).getTime() > fiveMinutesAgo
    )
    
    // If no active visitors, return empty array - no points on globe
    if (activeEvents.length === 0) return []
    
    const pointMap = new Map<string, { lat: number; lng: number; size: number; color: string; count: number }>()
    
    activeEvents
      .filter(e => e.country && countryCoordinates[e.country])
      .forEach(e => {
        const coords = countryCoordinates[e.country!]
        const key = e.country!
        
        if (pointMap.has(key)) {
          const existing = pointMap.get(key)!
          existing.count++
          existing.size = Math.min(0.12 + existing.count * 0.02, 0.35)
        } else {
          pointMap.set(key, {
            lat: coords.lat,
            lng: coords.lng,
            size: 0.12,
            color: isDark ? 'rgba(251, 146, 60, 0.7)' : 'rgba(234, 88, 12, 0.75)', // Primary amber - theme-aware
            count: 1
          })
        }
      })
    
    return Array.from(pointMap.values())
  }, [realtimeEvents, isDark])
  
  // Spawn elegant arcs from random origins TO visitor location
  const spawnActivityArcs = useCallback((events: RealtimeEvent[]) => {
    const newArcs: ActivityArc[] = []
    const newRings: ActivityRing[] = []
    
    events.forEach((event) => {
      if (!event.country || !countryCoordinates[event.country]) return
      
      const coords = countryCoordinates[event.country]
      // Pick a random origin point
      const origin = ARC_ORIGINS[Math.floor(Math.random() * ARC_ORIGINS.length)]
      
      // Theme-aware colors - more visible on light mode
      const color = event.type === 'session' 
        ? (isDark ? 'rgba(251, 146, 60, 0.6)' : 'rgba(234, 88, 12, 0.8)')  // Warm amber/orange - more visible in light
        : (isDark ? 'rgba(251, 146, 60, 0.4)' : 'rgba(234, 88, 12, 0.65)')  // Slightly more subtle but still visible
      
      newArcs.push({
        id: `arc-${event.id}-${Date.now()}`,
        startLat: origin.lat,
        startLng: origin.lng,
        endLat: coords.lat,
        endLng: coords.lng,
        color,
      })
      
      // Subtle expanding ring at the destination
      newRings.push({
        id: `ring-${event.id}-${Date.now()}`,
        lat: coords.lat,
        lng: coords.lng,
        color: isDark ? 'rgba(251, 146, 60, 0.4)' : 'rgba(234, 88, 12, 0.6)',
      })
    })
    
    setActivityArcs(prev => [...prev, ...newArcs])
    setActivityRings(prev => [...prev, ...newRings])
    
    // Remove after animation completes (longer for smoother feel)
    setTimeout(() => {
      setActivityArcs(prev => prev.filter(a => !newArcs.find(na => na.id === a.id)))
      setActivityRings(prev => prev.filter(r => !newRings.find(nr => nr.id === r.id)))
    }, 5000)
  }, [isDark])

  // Trigger visual feedback when new activity arrives
  const onNewActivity = useCallback((newEvents: RealtimeEvent[]) => {
    if (newEvents.length === 0) return
    spawnActivityArcs(newEvents)
  }, [spawnActivityArcs])

  const fetchRealtimeEvents = useCallback(async () => {
    // For aggregate mode, use propertyIds when available; otherwise use single propertyId
    const effectivePropertyIds = isAggregateMode && memoizedPropertyIds.length >= 1
      ? memoizedPropertyIds
      : propertyId
        ? [propertyId]
        : null

    if (!effectivePropertyIds || effectivePropertyIds.length === 0 || !token) return

    try {
      // Always use the aggregate endpoint — works for both single and multi-property
      const aggregateData = await getAggregateTrafficRealtime(effectivePropertyIds, token)
      setActiveVisitors(aggregateData.data?.active_visitors ?? null)
      const data = {
        current: { count: aggregateData.data?.active_visitors || 0 },
        liveFeed: aggregateData.data?.live_feed || [],
        sessions: aggregateData.data?.sessions || [],
        metrics: aggregateData.data?.metrics || [],
      }

      const processedEvents = parseRealtimeFeed(data)
      
      setRealtimeEvents(prev => {
        const existingIds = new Set(prev.map((e) => e.id))
        const newEvents = processedEvents.filter((e) => !existingIds.has(e.id))

        const isInitialLoad = prev.length === 0 && processedEvents.length > 0
        const eventsToAnimate = isInitialLoad 
          ? processedEvents.filter(e => {
              const eventTime = new Date(e.time).getTime()
              const twoMinutesAgo = Date.now() - 2 * 60 * 1000
              return eventTime > twoMinutesAgo
            }).slice(0, 5)
          : newEvents
        
        if (eventsToAnimate.length > 0) {
          setNewItemIds(prev => {
            const newIds = new Set(prev)
            eventsToAnimate.forEach((event) => newIds.add(event.id))
            return newIds
          })
          
          onNewActivity(eventsToAnimate)

          setTimeout(() => {
            setNewItemIds(prev => {
              const updated = new Set(prev)
              eventsToAnimate.forEach((event) => updated.delete(event.id))
              return updated
            })
          }, 3000)
        }

        return processedEvents
      })
    } catch (error) {
      console.error('Error fetching real-time events:', error)
    }
  }, [propertyId, token, isAggregateMode, memoizedPropertyIds, onNewActivity])

  const startAutoRefresh = useCallback(() => {
    if (refreshInterval.current) clearInterval(refreshInterval.current)
    refreshInterval.current = setInterval(fetchRealtimeEvents, 5000)
  }, [fetchRealtimeEvents])

  function getEventDescription(event: RealtimeEvent): string {
    if (event.type === 'session') {
      return event.page === '/' ? 'Homepage' : event.page
    } else if (event.action === 'click') {
      // Show more context for clicks
      const elementType = event.secondary
        ? event.secondary === 'link' ? 'link'
          : event.secondary === 'button' ? 'button'
          : event.secondary === 'nav-link' ? 'nav'
          : event.secondary
        : 'element'

      if (event.target) {
        return `Clicked ${elementType}: "${event.target}"`
      }
      return `Clicked ${elementType}`
    } else {
      return 'Form submitted'
    }
  }

  function getCountryName(countryCode: string): string {
    return countryCodes[countryCode] || countryCode
  }

  useEffect(() => {
    const hasProperty = propertyId || (isAggregateMode && memoizedPropertyIds.length >= 1)
    if (hasProperty && token) {
      fetchRealtimeEvents()
      startAutoRefresh()
    }
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current)
    }
  }, [propertyId, token, isAggregateMode, memoizedPropertyIds, fetchRealtimeEvents, startAutoRefresh])

  // Configure globe controls - only once on mount
  const globeInitialized = useRef(false)
  
  useEffect(() => {
    if (globeEl.current && !globeInitialized.current) {
      globeInitialized.current = true
      
      const controls = globeEl.current.controls()
      if (controls) {
        controls.autoRotate = true
        controls.autoRotateSpeed = 0.4
        controls.enableZoom = false
        controls.enablePan = false
        controls.enableDamping = true
        controls.dampingFactor = 0.1
      }
      // Set initial point of view - centered view (only once)
      globeEl.current.pointOfView({ lat: 20, lng: 0, altitude: 2.2 }, 0)
    }
  }, [GlobeComponent])

  /** Last 30 minutes of the feed: histogram buckets, per-kind counts, top page */
  const recentWindow = useMemo(() => {
    const now = Date.now()
    const from = now - HISTOGRAM_WINDOW_MS
    const bucketMs = HISTOGRAM_WINDOW_MS / HISTOGRAM_BUCKETS
    const buckets = new Array(HISTOGRAM_BUCKETS).fill(0) as number[]
    const counts = { views: 0, clicks: 0, forms: 0 }
    const pages: Record<string, number> = {}
    for (const e of realtimeEvents) {
      const t = new Date(e.time).getTime()
      if (!Number.isFinite(t) || t < from) continue
      const idx = Math.min(HISTOGRAM_BUCKETS - 1, Math.max(0, Math.floor((t - from) / bucketMs)))
      buckets[idx] += 1
      const kind = eventKind(e)
      counts[kind] += 1
      if (kind === 'views' && e.page) pages[e.page] = (pages[e.page] || 0) + 1
    }
    const topPage = Object.entries(pages).sort((a, b) => b[1] - a[1])[0] ?? null
    return { buckets, counts, topPage }
  }, [realtimeEvents])

  const profiles = useMemo(() => buildVisitorProfiles(realtimeEvents), [realtimeEvents])
  // The profile is a page under Analytics (`<base>/visitors/:key`); the date
  // range and filters ride along in the query so Back lands where they were.
  const openProfileKey = useCallback(
    (key: string) => {
      const base = location.pathname.replace(/\/[^/]+\/?$/, '')
      navigate({ pathname: `${base}/visitors/${encodeURIComponent(key)}`, search: location.search })
    },
    [location.pathname, location.search, navigate],
  )
  const openProfile = useCallback((event: RealtimeEvent) => openProfileKey(profileKeyOf(event)), [openProfileKey])

  if (isOverview) {
    const maxBucket = Math.max(...recentWindow.buckets, 0)
    const filtered = feedFilter === 'all'
      ? realtimeEvents
      : realtimeEvents.filter((e) => eventKind(e) === feedFilter)
    const limit = overviewExpanded ? 20 : 7
    const shown = filtered.slice(0, limit)

    const describe = (e: RealtimeEvent): { icon: any; action: string; detail?: string } => {
      const kind = eventKind(e)
      const page = e.page || '/'
      if (kind === 'views') return { icon: ViewIcon, action: `Viewed ${page}` }
      if (kind === 'clicks') {
        return {
          icon: Cursor01Icon,
          action: clickedPhrase(e.target, e.secondary),
          detail: `on ${page}`,
        }
      }
      return { icon: Note01Icon, action: 'Submitted a form', detail: `on ${page}` }
    }

    const statRows: { label: string; value: string }[] = [
      { label: 'Total views, 30 min', value: formatNumeric(recentWindow.counts.views) },
      { label: 'Clicks', value: formatNumeric(recentWindow.counts.clicks) },
      { label: 'Form submissions', value: formatNumeric(recentWindow.counts.forms) },
      {
        label: 'Top page now',
        value: recentWindow.topPage ? `${recentWindow.topPage[0]} · ${recentWindow.topPage[1]}` : '—',
      },
    ]

    return (
      <Card ref={containerRef} className={cn('gap-0 py-0', className)}>
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-[18px] py-3.5">
          <span className="size-2 shrink-0 rounded-full bg-live ring-4 ring-success-tint" aria-hidden />
          <OverviewCardTitle>Live activity</OverviewCardTitle>
          <span className="text-xs leading-4 text-muted-foreground">Updates as it happens</span>
          <span className="flex-1" />
          {liveView === 'activity' && (
            <OverviewTabs options={FEED_FILTERS} value={feedFilter} onChange={setFeedFilter} ariaLabel="Filter live activity" />
          )}
          <OverviewTabs options={LIVE_VIEWS} value={liveView} onChange={setLiveView} ariaLabel="Show activity or visitors" />
        </div>

        <div className="flex flex-col md:flex-row">
          {/* On site now */}
          <div className="flex shrink-0 flex-col gap-[18px] border-b border-border/60 bg-muted/50 p-[18px] md:w-[300px] md:border-b-0 md:border-r">
            <div className="flex flex-col gap-1">
              <span className="text-xs leading-4 text-muted-foreground">On site now</span>
              <AnimatedCounter
                value={activeVisitors ?? 0}
                className="text-[32px] font-semibold leading-10 tracking-[-0.03em] text-foreground"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex h-11 shrink-0 items-end gap-[3px]" aria-label="Activity in the last 30 minutes" role="img">
                {recentWindow.buckets.map((n, i) => {
                  const last = i === recentWindow.buckets.length - 1
                  const pct = maxBucket > 0 ? Math.max(8, Math.round((n / maxBucket) * 100)) : 8
                  return (
                    <span
                      key={i}
                      className={cn('flex-1 basis-0 rounded-[2px]', last ? 'bg-primary' : 'bg-primary-tint-ring')}
                      style={{ height: `${pct}%` }}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between text-xs leading-4 text-muted-foreground">
                <span>30 min ago</span>
                <span>now</span>
              </div>
            </div>

            <div className="flex flex-col">
              {statRows.map((row) => (
                <div key={row.label} className="flex justify-between gap-3 border-t border-border/60 py-2 text-xs leading-4">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="truncate font-medium text-foreground tabular-nums">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Feed */}
          <div className="flex min-w-0 flex-1 flex-col py-1.5">
            {liveView === 'visitors' ? (
              <LiveVisitorList profiles={profiles.slice(0, overviewExpanded ? 20 : 7)} onOpen={(p) => openProfileKey(p.key)} />
            ) : shown.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-12 text-center">
                <p className="text-[13px] text-foreground">Waiting for visitors</p>
                <p className="max-w-[260px] text-xs text-muted-foreground">
                  Page views, clicks and form submissions stream here as they happen.
                </p>
              </div>
            ) : (
              <AnimatePresence initial={false}>
                {shown.map((event, i) => {
                  const { icon, action, detail } = describe(event)
                  const countryName = event.country ? getCountryName(event.country) : ''
                  const name = visitorName(profileKeyOf(event))
                  const sub = [countryName, event.browser].filter(Boolean).join(' · ')
                  const source = event.utmSource?.trim()
                  const mobile = isMobileDevice(event.device)
                  return (
                    <motion.button
                      type="button"
                      key={event.id}
                      layout
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                      onClick={() => openProfile(event)}
                      aria-label={`Open visitor profile: ${name}, ${action}`}
                      className={cn(
                        'flex h-[58px] w-full shrink-0 items-center gap-3 px-[18px] text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
                        i > 0 && 'border-t border-border/50',
                        newItemIds.has(event.id) && 'bg-muted/40',
                      )}
                    >
                      <VisitorOrb visitorKey={profileKeyOf(event)} className="size-[30px]" />

                      <div className="flex w-[150px] shrink-0 flex-col gap-0.5 max-sm:w-[110px]">
                        <span className="truncate text-[13px] font-medium leading-4 text-foreground">{name}</span>
                        <span className="truncate text-xs leading-4 text-muted-foreground">{sub || 'Visitor'}</span>
                      </div>

                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <HugeiconsIcon icon={icon} size={14} className="shrink-0 text-muted-foreground" />
                        <span className="truncate text-[13px] leading-4 text-foreground">{action}</span>
                        {detail && (
                          <span className="hidden shrink-0 text-xs leading-4 text-muted-foreground lg:inline">{detail}</span>
                        )}
                      </div>

                      {source && (
                        <span className="hidden h-[22px] shrink-0 items-center gap-[5px] rounded-md px-2 ring-1 ring-foreground/[0.08] sm:flex">
                          <HugeiconsIcon icon={ArrowRight02Icon} size={10} className="text-muted-foreground" />
                          <span className="max-w-[96px] truncate text-xs leading-[14px] text-muted-foreground capitalize">
                            {source}
                          </span>
                        </span>
                      )}

                      <span className="hidden w-[18px] shrink-0 justify-center sm:flex" title={event.device}>
                        <HugeiconsIcon
                          icon={mobile ? SmartPhone01Icon : ComputerIcon}
                          size={14}
                          className="text-muted-foreground/70"
                        />
                      </span>

                      <span className="w-[34px] shrink-0 text-right text-xs leading-4 text-muted-foreground tabular-nums">
                        {shortElapsed(event.time)}
                      </span>
                    </motion.button>
                  )
                })}
              </AnimatePresence>
            )}
          </div>
        </div>

        {(liveView === 'visitors' ? profiles.length : filtered.length) > 7 && (
          <OverviewCardFooter
            label={overviewExpanded ? 'Show less' : liveView === 'visitors' ? 'Show more visitors' : 'Show more activity'}
            onClick={() => setOverviewExpanded((v) => !v)}
          />
        )}
      </Card>
    )
  }

  return (
    <Card 
      ref={containerRef}
      className={cn(
        "relative w-full overflow-hidden border-border/30 !py-0 !gap-0",
        compact ? "min-h-[280px] h-[280px]" : "min-h-[440px] h-[440px]",
        className
      )}
    >
      {/* THE GLOBE - Bottom-left anchor with top padding to avoid metric overlap */}
      {globeVisible && (
        <div className="absolute -bottom-[160px] -left-[60px] pointer-events-none z-0">
          {GlobeComponent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <GlobeComponent
                ref={globeEl}
                width={520}
                height={520}
                backgroundColor="rgba(0,0,0,0)"
                globeImageUrl={isDark
                  ? "//unpkg.com/three-globe/example/img/earth-dark.jpg"
                  : "//unpkg.com/three-globe/example/img/earth-day.jpg"
                }
                bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"

                // Visitor dots
                pointsData={pointsData}
                pointAltitude={0.02}
                pointColor="color"
                pointRadius={0.5}
                pointsMerge={false}

                // Arcs - the magic
                arcsData={activityArcs}
                arcStartLat={(d: any) => d.startLat}
                arcStartLng={(d: any) => d.startLng}
                arcEndLat={(d: any) => d.endLat}
                arcEndLng={(d: any) => d.endLng}
                arcColor={(d: any) => d.color}
                arcAltitude={0.3}
                arcStroke={1.2}
                arcDashLength={0.5}
                arcDashGap={0.2}
                arcDashAnimateTime={1500}

                // Impact rings
                ringsData={activityRings}
                ringLat={(d: any) => d.lat}
                ringLng={(d: any) => d.lng}
                ringColor={(d: any) => d.color}
                ringMaxRadius={4}
                ringPropagationSpeed={2}
                ringRepeatPeriod={1200}

                // Atmosphere glow
                showAtmosphere={true}
                atmosphereColor={isDark ? "rgba(251, 146, 60, 0.12)" : "rgba(234, 88, 12, 0.08)"}
                atmosphereAltitude={0.12}
              />
            </motion.div>
          )}

          {!GlobeComponent && (
            <div className="w-[520px] h-[520px] flex items-center justify-center">
              <motion.div
                className="w-3 h-3 rounded-full bg-orange-500/40"
                animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          )}
        </div>
      )}

      {/* Content overlay */}
      <div className="relative z-20 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-3 border-b border-border/40 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="relative flex size-1.5 shrink-0">
                <motion.span
                  className="absolute inline-flex size-full rounded-full bg-live"
                  animate={{ scale: [1, 1.8, 1], opacity: [0.55, 0, 0.55] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="relative inline-flex size-1.5 rounded-full bg-live" />
              </span>
              <h3 className="text-sm font-medium text-foreground tracking-tight">Live activity</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 pl-3.5">
              Real-time visitors
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {webglSupported && !compact && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleGlobe}
                    aria-label={showGlobe ? 'Hide globe' : 'Show globe'}
                    className={cn(
                      'inline-flex size-7 items-center justify-center rounded-md border border-border/40 transition-colors',
                      showGlobe
                        ? 'bg-muted text-foreground'
                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40',
                    )}
                  >
                    <HugeiconsIcon icon={GlobalIcon} size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {showGlobe ? 'Hide globe' : 'Show globe'}
                </TooltipContent>
              </Tooltip>
            )}

            {anomalyData && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 cursor-help">
                    <span className="text-xs text-muted-foreground">
                      vs avg
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs font-medium tabular-nums gap-0.5 px-1.5 py-0 h-5 border-border/40',
                        anomalyData.type === 'up'
                          ? 'bg-muted text-foreground'
                          : 'bg-warning/10 text-warning dark:text-warning',
                      )}
                    >
                      {anomalyData.type === 'up' ? '↑' : '↓'}
                      {anomalyData.percentChange.toFixed(0)}%
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px] p-3">
                  <p className="font-medium mb-1.5 text-sm">
                    {anomalyData.type === 'up' ? 'Traffic spike' : 'Traffic dip'}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Current traffic is{' '}
                    <span className="font-medium text-foreground">
                      {anomalyData.percentChange.toFixed(0)}%{' '}
                      {anomalyData.type === 'up' ? 'higher' : 'lower'}
                    </span>{' '}
                    than your typical average for this time.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Metrics strip */}
        <div className="flex items-center gap-3 sm:gap-4 px-4 py-2.5 border-b border-border/20 overflow-x-auto scrollbar-hide shrink-0">
          <div className="inline-flex items-center gap-1.5 shrink-0 rounded-md bg-success/10 px-2 py-1">
            <span className="relative flex size-1.5">
              <motion.span
                className="absolute inline-flex size-full rounded-full bg-live"
                animate={{ scale: [1, 1.7, 1], opacity: [0.45, 0, 0.45] }}
                transition={{ duration: 1.6, repeat: Infinity }}
              />
              <span className="relative inline-flex size-1.5 rounded-full bg-live" />
            </span>
            <span className="text-xs font-medium text-success dark:text-success">
              Active
            </span>
            <AnimatedCounter value={activeVisitors ?? 0} className="text-sm font-medium text-foreground leading-none" />
          </div>

          <div className="h-3.5 w-px bg-border/40 shrink-0" aria-hidden />

          <div className="inline-flex items-baseline gap-1.5 shrink-0">
            <span className="text-xs font-medium text-muted-foreground">
              Views
            </span>
            <span className="text-sm font-medium text-foreground tabular-nums leading-none">
              {eventCounts.sessions}
            </span>
          </div>

          <div className="h-3.5 w-px bg-border/40 shrink-0" aria-hidden />

          <div className="inline-flex items-baseline gap-1.5 shrink-0">
            <span className="text-xs font-medium text-muted-foreground">
              Clicks
            </span>
            <span className="text-sm font-medium text-foreground tabular-nums leading-none">
              {eventCounts.clicks}
            </span>
          </div>

          {topLocation && (
            <>
              <div className="h-3.5 w-px bg-border/40 shrink-0" aria-hidden />
              <div className="inline-flex items-center gap-1.5 shrink-0 min-w-0">
                <span className="text-xs font-medium text-muted-foreground">
                  Top
                </span>
                <span className="text-sm font-medium text-foreground flex items-center gap-1.5 min-w-0">
                  {topLocationFlag ? (
                    <span className="leading-none" aria-hidden>
                      {topLocationFlag}
                    </span>
                  ) : null}
                  <span className="truncate">
                    <span className="text-muted-foreground">{topLocation}</span>
                    <span className="text-muted-foreground/40 mx-1">·</span>
                    {getCountryName(topLocation)}
                  </span>
                </span>
              </div>
            </>
          )}
        </div>

        {/* Main content */}
        <div className="flex flex-1 min-h-0 px-4 pt-3 pb-3.5">
          {globeVisible && <div className="hidden sm:block sm:w-[40%] shrink-0" />}

          <div
            className={cn(
              'flex-1 flex flex-col min-h-0 rounded-lg border border-border bg-card overflow-hidden',
              globeVisible && 'sm:ml-4',
            )}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/30 bg-muted/20 shrink-0">
              <span className="text-xs font-medium text-foreground">Recent activity</span>
              {realtimeEvents.length > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {realtimeEvents.length} event{realtimeEvents.length === 1 ? '' : 's'}
                </span>
              )}
            </div>

            <ScrollArea className="flex-1">
              <div>
                <AnimatePresence mode="popLayout" initial={false}>
                  {realtimeEvents.slice(0, feedDisplayLimit).map((event, i) => (
                    <ActivityEvent
                      key={event.id}
                      event={event}
                      isNew={newItemIds.has(event.id)}
                      index={i}
                      getEventDescription={getEventDescription}
                      getElapsedTime={getElapsedTime}
                      getCountryName={getCountryName}
                      onOpen={() => openProfile(event)}
                    />
                  ))}

                  {realtimeEvents.length > feedDisplayLimit && (
                    <div className="flex items-center justify-center gap-2 py-2.5 border-t border-border/20">
                      {feedDisplayLimit < 100 && (
                        <button
                          type="button"
                          onClick={() => setFeedDisplayLimit((prev) => Math.min(prev + 20, 100))}
                          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1 rounded-md hover:bg-muted/50"
                        >
                          Show more
                        </button>
                      )}
                      {feedDisplayLimit < realtimeEvents.length && (
                        <button
                          type="button"
                          onClick={() => setFeedDisplayLimit(100)}
                          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1 rounded-md hover:bg-muted/50"
                        >
                          Show all ({realtimeEvents.length})
                        </button>
                      )}
                    </div>
                  )}

                  {realtimeEvents.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center gap-2 py-10 px-4"
                    >
                      <span className="relative flex size-2">
                        <motion.span
                          className="absolute inline-flex size-full rounded-full bg-success/50"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0, 0.4] }}
                          transition={{ duration: 2.2, repeat: Infinity }}
                        />
                        <span className="relative size-2 rounded-full bg-success/40" />
                      </span>
                      <p className="text-xs text-muted-foreground">Waiting for visitors…</p>
                      <p className="text-xs text-muted-foreground/60 text-center max-w-[200px]">
                        New sessions and clicks will stream here as they happen.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </Card>
  )
}


/** One row per visit: place and device, where they are now, how much they've done. */
export function LiveVisitorList({ profiles, onOpen }: { profiles: VisitorProfile[]; onOpen: (p: VisitorProfile) => void }) {
  const now = useNow()
  if (profiles.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-12 text-center">
        <p className="text-[13px] text-foreground">No visitors yet</p>
        <p className="max-w-[260px] text-xs text-muted-foreground">Each visit shows up here with its pages, clicks and forms.</p>
      </div>
    )
  }
  return (
    <ul className="flex flex-col">
      {profiles.map((p, i) => (
        <li key={p.key} className={cn(i > 0 && 'border-t border-border/50')}>
          <button
            type="button"
            onClick={() => onOpen(p)}
            className="flex h-[58px] w-full items-center gap-3 px-[18px] text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
          >
            <VisitorOrb visitorKey={p.key} className="size-[30px]" />
            <div className="flex w-[150px] shrink-0 flex-col gap-0.5 max-sm:w-[110px]">
              <span className="truncate text-[13px] font-medium leading-4">{visitorName(p.key)}</span>
              <span className="truncate text-xs leading-4 text-muted-foreground">
                {[nameOfCountry(p.country), p.browser || p.deviceGroup].filter(Boolean).join(' · ') || 'Visitor'}
              </span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-[13px] leading-4">Now on {p.currentPage}</span>
              <span className="truncate text-xs leading-4 text-muted-foreground tabular-nums">
                {p.views} {p.views === 1 ? 'page' : 'pages'} · {p.clicks} {p.clicks === 1 ? 'click' : 'clicks'}
                {p.forms > 0 ? ` · ${p.forms} ${p.forms === 1 ? 'form' : 'forms'}` : ''}
                {p.source ? ` · from ${p.source}` : ''}
              </span>
            </div>
            <span className="hidden sm:flex">
              <OnSitePill profile={p} now={now} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
